import { ColumnModel, FilterModel, PermissionValues, useCurrentAccount } from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { array, string, object as yupObject } from 'yup';
import { Layout } from '../../styles';
import { Colors } from '../../themes';
import { Button } from '../crop-circle/Button';
import { colors as CropCircleColors } from '../crop-circle/tokens';
import { FormContext } from '../forms';
import { BottomComponentBar } from '../navigation/bottomComponentBar';
import type { BottomComponentBarBaseProps } from '../navigation/types';
import { useSlideLayoutSafe } from '../slide-layout';
import { filterScreenStyles } from './filterScreenStyles';

export type ColumnsFilterScreenHandle = {
  onReset: () => Promise<void>;
};

export type ColumnsFilterScreenProps = {
  mode: 'page' | 'modal';
  filter: (FilterModel & ColumnModel) | undefined;
  setFilter: (filter: any) => Promise<any>;
  validateListOrder: (filter: any) => Promise<any>;
  createDefaultFilter: () => FilterModel & ColumnModel;
  createFilter: (data: any) => FilterModel & ColumnModel;
  baseColumns: string[];
  adminColumns?: string[];
  i18nPrefix: string;
  viewResultsTranslationKey: string;
  countQuery?: string;
  buildCountVariables?: (filter: any) => any;
  countDataPath?: string;
  onBack?: () => void;
  bottomBar?: React.ComponentType<BottomComponentBarBaseProps>;
};

const formSchema = yupObject({
  columns: array().of(string().optional().nullable()).optional().nullable(),
});

export const ColumnsFilterScreen = forwardRef<
  ColumnsFilterScreenHandle,
  ColumnsFilterScreenProps
>(function ColumnsFilterScreen(props, ref) {
  const {
    filter,
    setFilter,
    validateListOrder,
    createDefaultFilter,
    createFilter,
    baseColumns,
    adminColumns,
    i18nPrefix,
    viewResultsTranslationKey,
    onBack,
    bottomBar,
  } = props;

  const { account } = useCurrentAccount();
  const isGlobalAdmin = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  const { t } = useTranslation();
  const slideLayout = useSlideLayoutSafe();
  const BottomBar = bottomBar ?? BottomComponentBar;
  const filterTitle = t('common.filter');

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      columns: (() => {
        if (!filter?.columns) return ['name'];
        const cols = Array.from(
          new Set(
            (filter.columns as any[]).filter(
              (c: any): c is string => c != null && typeof c === 'string'
            )
          )
        );
        return cols.includes('name') ? cols : ['name', ...cols];
      })(),
    },
  });

  const { handleSubmit, watch, reset } = formMethods;
  const onSubmitRef = useRef(onSubmit);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Auto-apply on web (debounced)
  useEffect(() => {
    if (Platform.OS === 'web') {
      const subscription = watch(() => {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          handleSubmit(onSubmitRef.current)();
        }, 1000);
      });
      return () => {
        subscription.unsubscribe();
        clearTimeout(debounceTimerRef.current);
      };
    }
    return;
  }, [watch, handleSubmit]);

  // Update slide layout title with "Filter" text
  useEffect(() => {
    slideLayout?.setColumnTitle(filterTitle);
  }, [slideLayout, filterTitle]);

  const resetForm = useCallback(() => {
    const defaultFilter = createDefaultFilter();
    let defaultColumns = defaultFilter.columns || ['name'];
    if (!defaultColumns.includes('name'))
      defaultColumns = ['name', ...defaultColumns];
    reset({ columns: defaultColumns });
  }, [reset, createDefaultFilter]);

  useEffect(() => {
    if (!filter) resetForm();
  }, [filter, resetForm]);

  async function onSubmit(data: any) {
    if (data.columns) {
      let columns = Array.from(
        new Set(
          (data.columns as any[]).filter(
            (c: any): c is string => c != null && typeof c === 'string'
          )
        )
      );
      if (!columns.includes('name')) columns = ['name', ...columns];
      data.columns = columns;
    }
    const filterModel = createFilter({ ...filter, ...data });
    await Promise.all([setFilter(filterModel), validateListOrder(filterModel)]);
    onBack?.();
  }
  onSubmitRef.current = onSubmit;

  const onReset = useCallback(async () => {
    resetForm();
    const defaultFilter = createDefaultFilter();
    await Promise.all([
      setFilter(defaultFilter),
      validateListOrder(defaultFilter),
    ]);
  }, [resetForm, setFilter, validateListOrder, createDefaultFilter]);

  useImperativeHandle(ref, () => ({ onReset }), [onReset]);

  const canReset = (): boolean => {
    const formData = watch();
    const defaultFilter = createDefaultFilter();
    return (
      JSON.stringify(formData.columns?.sort()) !==
      JSON.stringify(defaultFilter.columns?.sort())
    );
  };

  const footer = (
    <BottomBar
      style={{ paddingLeft: 0, paddingRight: 8 }}
      backButton={<View />}
      additionalHeaderBlock={
        <Button
          style={
            [
              filterScreenStyles.viewResultsButton,
              !canReset() && filterScreenStyles.viewResultsButtonWithMargin,
            ] as any
          }
          onPress={handleSubmit(onSubmit)}
          state="active"
        >
          {t(viewResultsTranslationKey)}
        </Button>
      }
      onRightPress={canReset() ? onReset : undefined}
    />
  );

  return (
    <FormContext.Provider value={formMethods as any}>
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            filterScreenStyles.scrollContentPage,
          ]}
        >
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t(`${i18nPrefix}.columnSelection`)}
            </Text>
            <Controller
              control={formMethods.control}
              name="columns"
              render={({ field: { onChange, value } }) => {
                const currentValue = (value || []).filter(
                  (c): c is string => c != null
                );
                const selectableColumns = baseColumns.filter(
                  (col) => col !== 'name'
                );
                const onPressColumn = (column: string) => {
                  if (column === 'name') return;
                  let newValue: string[];
                  if (currentValue.includes(column))
                    newValue = currentValue.filter((c) => c !== column);
                  else newValue = [...currentValue, column];
                  if (!newValue.includes('name'))
                    newValue = ['name', ...newValue];
                  onChange(newValue);
                };
                const effectiveValue = currentValue.includes('name')
                  ? currentValue
                  : ['name', ...currentValue];
                return (
                  <View style={styles.optionsContainer}>
                    <Pressable
                      key="name"
                      disabled={true}
                      style={[
                        styles.optionChip,
                        styles.optionChipSelected,
                        styles.optionChipDisabled,
                      ]}
                    >
                      <Text
                        style={[
                          styles.optionChipText,
                          styles.optionChipTextSelected,
                        ]}
                      >
                        {t(`${i18nPrefix}.columns.name`)}
                      </Text>
                    </Pressable>
                    {selectableColumns.map((column) => {
                      const isSelected = effectiveValue.includes(column);
                      return (
                        <Pressable
                          key={column}
                          onPress={() => onPressColumn(column)}
                          style={[
                            styles.optionChip,
                            isSelected && styles.optionChipSelected,
                          ]}
                        >
                          <Text
                            style={[
                              styles.optionChipText,
                              isSelected && styles.optionChipTextSelected,
                            ]}
                          >
                            {t(`${i18nPrefix}.columns.${column}`)}
                          </Text>
                        </Pressable>
                      );
                    })}
                    {isGlobalAdmin &&
                      adminColumns?.map((column) => {
                        const isSelected = effectiveValue.includes(column);
                        return (
                          <Pressable
                            key={column}
                            onPress={() => onPressColumn(column)}
                            style={[
                              styles.optionChip,
                              isSelected && styles.optionChipSelected,
                            ]}
                          >
                            <Text
                              style={[
                                styles.optionChipText,
                                isSelected && styles.optionChipTextSelected,
                              ]}
                            >
                              {t(`${i18nPrefix}.columns.${column}`)}
                            </Text>
                          </Pressable>
                        );
                      })}
                  </View>
                );
              }}
            />
          </View>
        </ScrollView>
        {footer}
      </View>
    </FormContext.Provider>
  );
});

const styles = StyleSheet.create({
  container: {
    ...Layout.container,
    backgroundColor: Colors.white,
    flex: 1,
    position: 'relative',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 100,
  },
  section: {
    paddingTop: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: CropCircleColors.neutral[600],
    marginBottom: 12,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: CropCircleColors.neutral[200],
    gap: 6,
  },
  optionChipSelected: {
    backgroundColor: Colors.black,
    borderColor: Colors.black,
  },
  optionChipDisabled: {
    opacity: 0.6,
  },
  optionChipText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.black,
  },
  optionChipTextSelected: {
    color: Colors.white,
  },
});
